import { AxiosError } from 'axios'
import { SelectProps } from 'components/Select'
import { ENDPOINTS } from 'constants/endpoints'
import apiInstance from 'services/api/instance'

import { handleErrorMessage } from '../errors'

export const getAll = async (): Promise<BudgetApiResponse[]> => {
  try {
    const getAllResponse = await apiInstance.get<BudgetApiResponse[]>(
      ENDPOINTS.BUDGETS.GET_ALL,
    )

    return getAllResponse.data
  } catch (getAllError) {
    const axiosError = getAllError as AxiosError
    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const getAllOptions = async (): Promise<
  Pick<BudgetApiResponse, 'id' | 'name'>[]
> => {
  try {
    const getAllResponse = await apiInstance.get<BudgetApiResponse[]>(
      ENDPOINTS.BUDGETS.GET_ALL_OPTIONS,
    )

    return getAllResponse.data
  } catch (getAllError) {
    const axiosError = getAllError as AxiosError
    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const getAllNamesAndIdsCurrentYear = async ({
  budget_id,
}: BudgetsGetNameAndIdsCurrentYearApiRequest): Promise<BudgetApiResponse[]> => {
  try {
    const getAllResponse = await apiInstance.post<BudgetApiResponse[]>(
      ENDPOINTS.BUDGETS.GET_NAME_AND_IDS_CURRENT_YEAR,
      {
        budget_id,
      },
    )

    return getAllResponse.data
  } catch (getAllError) {
    const axiosError = getAllError as AxiosError
    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const getAllNamesAndIds = async (): Promise<SelectProps['options']> => {
  try {
    const getAllResponse = await getAllOptions()
    return (getAllResponse || []).map((paymentGateway) => ({
      label: paymentGateway.name,
      value: paymentGateway.id.toString(),
    }))
  } catch (getAllError) {
    const axiosError = getAllError as AxiosError
    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const get = async (id: string): Promise<BudgetApiResponse> => {
  try {
    const getResponse = await apiInstance.get<BudgetApiResponse>(
      ENDPOINTS.BUDGETS.GET(id),
    )

    return getResponse.data
  } catch (getError) {
    const axiosError = getError as AxiosError
    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const duplicate = async (id: string): Promise<BudgetApiResponse> => {
  try {
    const duplicateResponse = await apiInstance.get<BudgetApiResponse>(
      ENDPOINTS.BUDGETS.DUPLICATE(id),
    )

    return duplicateResponse.data
  } catch (getError) {
    const axiosError = getError as AxiosError
    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const create = async (
  budget: BudgetsApiRequest,
): Promise<BudgetApiResponse> => {
  try {
    const creationResponse = await apiInstance.post<BudgetApiResponse>(
      ENDPOINTS.BUDGETS.CREATE,
      budget,
    )

    return creationResponse.data
  } catch (creationError) {
    const axiosError = creationError as AxiosError

    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const update = async ({
  id,
  budget,
}: {
  budget: BudgetsApiRequest
  id: string
}): Promise<BudgetApiResponse> => {
  try {
    const updateResponse = await apiInstance.put<BudgetApiResponse>(
      ENDPOINTS.BUDGETS.UPDATE(id),
      budget,
    )

    return updateResponse.data
  } catch (updateError) {
    const axiosError = updateError as AxiosError

    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const remove = async (id: string) => {
  try {
    const deleteResponse = await apiInstance.delete(
      ENDPOINTS.BUDGETS.DELETE(id),
    )

    return deleteResponse.data
  } catch (deleteError) {
    const axiosError = deleteError as AxiosError

    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const sendMessageMail = async ({
  id,
  data,
}: {
  id: number
  data: {
    sendTo: string
  }
}) => {
  try {
    const sendResponse = await apiInstance.post(
      ENDPOINTS.BUDGETS.SEND_MESSAGE_MAIL(String(id)),
      data,
    )

    return sendResponse.data
  } catch (sendError) {
    const axiosError = sendError as AxiosError<{ message: string }>

    const customErrors = axiosError?.response?.data?.message
      ? { 404: { status: 404, message: axiosError?.response?.data?.message } }
      : []

    return Promise.reject(handleErrorMessage({ axiosError, customErrors }))
  }
}

export const downloadProposal = async ({ id }: { id: number }) => {
  try {
    const sendResponse = await apiInstance.get(
      ENDPOINTS.BUDGETS.DOWNLOAD_PROPOSAL(String(id)),
      {
        responseType: 'arraybuffer',
      },
    )

    return sendResponse
  } catch (sendError) {
    const axiosError = sendError as AxiosError<{ message: string }>

    return Promise.reject(handleErrorMessage({ axiosError }))
  }
}

export const updateManyStatus = async (data: {
  budget_ids: number[]
  new_status: string
}) => {
  try {
    const sendResponse = await apiInstance.post(
      ENDPOINTS.BUDGETS.UPDATE_MANY_STATUS,
      data,
    )

    return sendResponse.data
  } catch (sendError) {
    const axiosError = sendError as AxiosError<{ message: string }>

    const customErrors = axiosError?.response?.data?.message
      ? { 404: { status: 404, message: axiosError?.response?.data?.message } }
      : []

    return Promise.reject(handleErrorMessage({ axiosError, customErrors }))
  }
}
